import _find from 'lodash/find';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';
import moment from 'moment';
import { defaultConfig } from '@bytebrand/fe-financing';
import { checkRangeValuesOnEqual } from '@bytebrand/fe-ui-core-autobahn/utils';
import { creationDateOptions, Filters as DEFAULT_FILTERS } from '../factories/FilterFactory';
import { RANGE_FILTERS, OBJECT_FILTERS, MODEL_FIELD_NAME, MMS_GROUP_NAME, MMS_GROUPS_KEY, MM_GROUPS_EXCLUDE_KEY, SERIES_KEY, SERIES_EXCLUDE_KEY, MM_EXCLUDE_GROUP_NAME, MMS_GROUP_KEYS_SET, ALL_DOORS_KEY, RATE_KEY } from '../constants/search';
export const filterRemover = (filters, keys) => {
    const queue = [];
    keys.forEach((key) => {
        if (MMS_GROUP_KEYS_SET.has(key)) {
            filters[MMS_GROUPS_KEY].forEach((filter) => {
                queue.push([filter[key], DEFAULT_FILTERS[MMS_GROUPS_KEY][0][key].value]);
            });
        }
        else {
            queue.push([filters[key], DEFAULT_FILTERS[key].value]);
        }
    });
    return function remove() {
        const [filter, defaultValue] = queue.shift();
        if (!filter) {
            return false;
        }
        if (Array.isArray(filter.value)) {
            if (filter.value.length === 0) {
                return remove();
            }
            filter.value.pop();
            if (filter.value.length > 0) {
                queue.unshift([filter, defaultValue]);
            }
            return true;
        }
        if (filter.isRange) {
            if (filter.value.from === defaultValue.from && filter.value.to === defaultValue.from) {
                return remove();
            }
            filter.value = Object.assign({}, defaultValue);
            return true;
        }
        if (filter.value === defaultValue) {
            return remove();
        }
        filter.value = defaultValue;
        return true;
    };
};
export const transformFiltersToQuery = (filters) => {
    const query = {
        filters: {
            absolutValues: {},
            noAbsolutValues: {},
            rangeValues: {},
            dateValues: {},
            ignoredValues: {}
        }
    };
    Object.keys(filters).forEach((key) => {
        const filterField = filters[key].field;
        const filterValue = filters[key].value;
        const isAbsolute = filters[key].isAbsolute;
        const isRange = filters[key].isRange;
        const isDate = filters[key].isDate;
        const isIgnored = filters[key].isIgnored;
        const isValueMustBeArray = filters[key].isValueMustBeArray;
        if (key === MMS_GROUPS_KEY) {
            const mmsGroups = filters[key];
            mmsGroups.map((group, index) => {
                Object.keys(group).forEach((mmsKey) => {
                    const mmsGroupField = group[mmsKey].field;
                    const mmsGroupValue = group[mmsKey].value;
                    if (!query.filters.absolutValues[`${MMS_GROUP_NAME}${index}`] && mmsGroupValue) {
                        query.filters.absolutValues[`${MMS_GROUP_NAME}${index}`] = {};
                    }
                    if (mmsGroupValue) {
                        query.filters.absolutValues[`${MMS_GROUP_NAME}${index}`][mmsGroupField] = mmsGroupValue;
                    }
                });
                if (group[SERIES_KEY].value) {
                    delete query.filters.absolutValues[`${MMS_GROUP_NAME}${index}`][MODEL_FIELD_NAME];
                }
            });
        }
        else if (key === MM_GROUPS_EXCLUDE_KEY) {
            const excludeGroups = filters[key];
            excludeGroups.map((group, index) => {
                Object.keys(group).forEach((excludeKey) => {
                    const excludeGroupField = group[excludeKey].field;
                    const excludeGroupValue = group[excludeKey].value;
                    if (!query.filters.absolutValues[`${MM_EXCLUDE_GROUP_NAME}${index}`] && excludeGroupValue) {
                        query.filters.absolutValues[`${MM_EXCLUDE_GROUP_NAME}${index}`] = {};
                    }
                    if (excludeGroupValue) {
                        query.filters.absolutValues[`${MM_EXCLUDE_GROUP_NAME}${index}`][excludeGroupField] = excludeGroupValue;
                    }
                });
                if (group[SERIES_EXCLUDE_KEY].value) {
                    delete query.filters.absolutValues[`${MM_EXCLUDE_GROUP_NAME}${index}`][MODEL_FIELD_NAME];
                }
            });
        }
        else if (key === 'SIMILAR_TO') {
            // comparable-cars anchors — a top-level query field of its own, not an ES field
            // mapping, so it bypasses the absolutValues/rangeValues machinery entirely.
            if (Array.isArray(filterValue) && filterValue.length) {
                query.filters.similarTo = filterValue;
            }
        }
        else if (isAbsolute && filterValue) {
            if (filterField === 'mainData_doors') {
                if (_isEqual(filterValue, [ALL_DOORS_KEY]))
                    return;
                const doorsCount = Array.isArray(filterValue) && filterValue.length ? filterValue.map((filter) => _find(filters.DOORS.options, o => o.key === filter).value) : null;
                query.filters.absolutValues.mainData_doors = doorsCount;
                return;
            }
            if (filterField === 'metaData_ownerId') {
                query.filters.absolutValues.metaData_ownerId = filters.OWNER_ID.value;
                return;
            }
            if (filterField === 'metaData_vehicleId') {
                query.filters.absolutValues.metaData_vehicleId = filters.VEHICLE_ID.value;
            }
            if (filterField === '_id') {
                // CAR_IDS defaults to [] (truthy — unlike every sibling isAbsolute filter's ''
                // default), so an explicit length check is needed here: an empty _id terms query
                // would match ZERO cars and silently break every other search on the SRP.
                if (!Array.isArray(filterValue) || !filterValue.length)
                    return;
                query.filters.absolutValues._id = filterValue;
                return;
            }
            if (filterField === 'offer_offerSource') {
                query.filters.absolutValues.offer_offerSource = filters.OFFER_SOURCE.value;
                return;
            }
            if (filterField === 'mainData_numberOfPreviousOwners') {
                if (filterValue === 'all_owners')
                    return;
                const ownersCount = _find(filters.OWNERS.options, o => o.key === filterValue);
                query.filters.absolutValues.mainData_numberOfPreviousOwners = ownersCount ? ownersCount.value : null;
                return;
            }
            if (filterField === 'dates.metaData_creationDate') {
                const daysCount = creationDateOptions.find(option => option.label === filterValue).count;
                if (!daysCount || typeof daysCount !== 'number')
                    return;
                const targetDate = new Date(Date.now() - daysCount * 24 * 60 * 60 * 1000); // eslint-disable-line
                query.filters.absolutValues['dates.metaData_creationDate'] = ['<', targetDate.toISOString()];
                return;
            }
        }
        else if (isRange) {
            const defaultValue = RANGE_FILTERS[key].defaultValue;
            const valueIsDefault = checkRangeValuesOnEqual(defaultValue, filterValue);
            if (!valueIsDefault) {
                // check value in from / to. If we have - add it
                Object.keys(filterValue).map((key) => {
                    if (filterValue[key]) {
                        const fieldName = filterField === 'engineData_power' ? `${filterField}${filterValue.type || 'PS'}` : filterField;
                        if (!query.filters.rangeValues[fieldName]) {
                            query.filters.rangeValues[fieldName] = {};
                        }
                        if (key !== 'type') {
                            query.filters.rangeValues[fieldName][key] = filterValue[key];
                        }
                    }
                });
            }
        }
        else if (isDate && filterValue) {
            if (filterField === 'careService_generalInspection') {
                const generalInspectionMonthValue = _find(filters.GENERAL_INSPECTION.options, o => o.key === filterValue).value;
                const resultTimestamp = moment().add(generalInspectionMonthValue, 'months').unix();
                const result = { from: resultTimestamp };
                query.filters.dateValues.careService_generalInspection = result;
                return;
            }
        }
        else if (isIgnored) {
            query.filters.ignoredValues[key] = filterValue;
        }
        else {
            // the value is Array and not empty
            if (Array.isArray(filterValue) && filterValue.length && !isValueMustBeArray) {
                query.filters.noAbsolutValues[key] = filterValue;
                // the value is Array and not empty and must be an array
            }
            else if (Array.isArray(filterValue) && filterValue.length && isValueMustBeArray) {
                filterValue.map((v) => { query.filters.noAbsolutValues[`${key}_${v}`] = [v]; });
            }
            else if (filterValue && !Array.isArray(filterValue)) {
                query.filters.noAbsolutValues[key] = [filterValue];
            }
        }
    });
    return query;
};
export const resolveMmsValue = (value) => {
    // Special case for https://vicus-group.atlassian.net/browse/ACF-1710
    // When user clicks backspace in field, for some reason values become empty array.
    if (Array.isArray(value) && !value.length) {
        return '';
    }
    // Test if there any characters that is not a space or line break
    const stringOnlySpaces = !(/\S/).test(value);
    if (stringOnlySpaces) {
        return '';
    }
    // Just return value as it was before. Without any transformations.
    return value;
};
// Durations that have a precomputed lang_neutral.rate_monthlyInstallment{d} field in
// Elasticsearch (ADV-205, written by car-basic-data's elasticRateEnricher).
export const RATE_SEARCH_DURATIONS = [12, 24, 36, 48, 60, 72];
// The indexed per-duration rates assume the car's stored (0 €) down payment. A user
// down payment lowers every displayed rate by its own annuity, so the searched band is
// shifted up by that equivalent instead. Uses the platform nominal rate — for dealer-rate
// cars this is an approximation, but it errs by at most a few € on the boundary.
const monthlyEquivalent = (amount, months) => {
    if (!(amount > 0) || !(months > 0))
        return 0;
    const r = defaultConfig.financing.nominalInterestRate / 100 / 12;
    return amount * r / (1 - (1 + r) ** -months);
};
export const resolveRateSearchDuration = (paybackPeriod) => {
    const requested = Number(paybackPeriod) || 60;
    return RATE_SEARCH_DURATIONS.includes(requested) ? requested : 60;
};
// Substitutes the {PP} placeholder in RATE_ASCENDING/RATE_DESCENDING sort queries with
// the duration selected in the RATE filter, so sorting uses the same per-duration field
// the filter and the card use.
export const resolveSortQuery = (sortQuery, filters) => sortQuery.replace('{PP}', String(resolveRateSearchDuration(_get(filters, `${RATE_KEY}.paybackPeriod`))));
// ADV-205: the RATE filter used to be approximated as a price_currentSalesPrice band
// (getRateSearchPrice) with a financing model that diverged from the card display.
// It now filters the indexed per-duration rate directly, so the boundary is exact
// and matches the rate shown on the card for every car, dealer rates included.
export const transformRateToPrice = (query) => {
    const rate = _get(query, `filters.ignoredValues.${RATE_KEY}`, {});
    const defaultRate = OBJECT_FILTERS[RATE_KEY].defaultValue;
    const rateIsDefault = checkRangeValuesOnEqual(defaultRate, rate);
    if (Object.keys(rate).length && !rateIsDefault) {
        const paybackPeriod = resolveRateSearchDuration(rate.paybackPeriod);
        const shift = Math.round(monthlyEquivalent(Number(rate.firstInstallment) || 0, paybackPeriod));
        const from = Number(rate.monthlyInstallmentFrom) || 0;
        const to = Number(rate.monthlyInstallmentTo) || 0;
        if (from > 0 || to > 0) {
            query.filters.rangeValues[`rate_monthlyInstallment${paybackPeriod}`] = {
                from: from > 0 ? from + shift : null,
                to: to > 0 ? to + shift : null,
            };
        }
    }
    delete query.filters.ignoredValues;
    return query;
};
